import { Dictionary } from 'vuex';

import { PermissionWithEntity } from '@/interfaces/permissionWithEntity';
import { AuthResource, authResource } from '@/resources/auth.resource';
import { AbstractModule } from '../abstract.module';

/**
 * Moduł sprawdzania uprawnień do konkretnej encji
 *
 * @class CheckPermissionForExactEntityModule
 * @extends {AbstractModule<AuthResource>}
 */
class CheckPermissionForExactEntityModule extends AbstractModule<AuthResource> {
  /**
   * Timeout kolejki pobierania
   *
   * @private
   * @type {*}
   * @memberof AuthModule
   */
  private permissionQueueTimeout: any;

  /**
   * Lista uprawnień do pobrania
   *
   * @private
   * @type {string[]}
   * @memberof AuthModule
   */
  private toCheck: string[] = [];

  /**
   * Lista resolverów promisów do wywołania
   *
   * @private
   * @memberof AuthModule
   */
  private resolvers: Array<() => any> = [];

  public namespaced = false;

  public state = { permissions: {} };

  public mutations = {
    /**
     * Zapisuje uprawnienia
     *
     * @param {*} localState
     * @param {{permissions: any[], result: boolean[]}} data
     */
    savePermissions: (localState: any, data: { permissions: any[]; result: boolean[] }): void => {
      const permissionsObj = data.permissions.reduce((obj: any, permission: any, index: number) => {
        obj[JSON.stringify(permission)] = data.result[index];
        return obj;
      }, {});
      localState.permissions = { ...localState.permissions, ...permissionsObj };
      this.resolvers.forEach(resolver => resolver());
      this.resolvers = [];
    },

    /**
     * Czyści zapisane uprawnienia
     *
     * @param {*} localState
     */
    clearPermissions: (localState: any): void => {
      localState.permissions = {};
      this.toCheck = [];
    }
  };

  public actions = {
    /**
     * Sprawdzenie uprawnień synchroniczne
     *
     * @param {*} context
     * @param {(PermissionWithEntity | PermissionWithEntity[])} permissions
     * @returns {Promise<Dictionary<boolean>>}
     */
    checkPermissionsForExactEntitySync: async (
      context: any,
      permissions: PermissionWithEntity | PermissionWithEntity[]
    ): Promise<Dictionary<boolean>> => {
      if (!Array.isArray(permissions)) {
        permissions = [permissions];
      }

      const toCheck = permissions.filter(
        permission => context.state.permissions[JSON.stringify(permission)] === undefined
      );

      const getResult = () =>
        (permissions as any[]).reduce((obj, permission) => {
          const permissionKey = JSON.stringify(permission);
          return {
            ...obj,
            [permissionKey]: context.state.permissions[permissionKey]
          };
        }, {});

      if (!toCheck.length) {
        return getResult();
      }

      return this.resource
        .checkPermissionsForExactEntities(toCheck)
        .then(result => context.commit('savePermissions', { permissions: toCheck, result }))
        .then(() => getResult());
    }
  };

  private queuePermissionsCheck(context: any, permissions: string[]): Promise<void> {
    if (this.permissionQueueTimeout) {
      clearTimeout(this.permissionQueueTimeout);
    }

    this.toCheck.push(...permissions.filter(permission => !this.toCheck.includes(permission)));

    return new Promise(resolve => {
      this.resolvers.push(resolve);
      this.permissionQueueTimeout = setTimeout(() => {
        const toCheck = this.toCheck.filter(permission => context.state.permissions[permission] === undefined);
        this.resource
          .checkPermissions(toCheck)
          .then(result => context.commit('savePermissions', { permissions: toCheck, result }));
      }, 0);
    });
  }
}

export default new CheckPermissionForExactEntityModule(authResource);
